import axios from "axios"
import { NextResponse } from "next/server"
import { z } from "zod"
import { clearSession, getSession } from "@/services/auth-service"
import {
  getWordPressAccountOrders,
  type WordPressMyOrderItem,
} from "@/services/wordpress-service"

const NO_CACHE_HEADERS = {
  "Cache-Control": "no-store",
} as const

const limitSchema = z.coerce.number().int().min(1).max(50)

const toUnauthorizedResponse = () => {
  return NextResponse.json(
    {
      ok: false,
      code: "UNAUTHORIZED",
      message: "Sessao nao autenticada.",
    },
    { status: 401, headers: NO_CACHE_HEADERS }
  )
}

const isUnauthorizedAxiosError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false
  }

  const status = error.response?.status

  return status === 401 || status === 403
}

const toOrderItemsArray = (
  items: WordPressMyOrderItem["items"]
): Array<{ quantity?: number | string }> => {
  if (!items) {
    return []
  }

  if (Array.isArray(items)) {
    return items
  }

  return Object.values(items)
}

const toItemCount = (items: WordPressMyOrderItem["items"]): number => {
  const normalizedItems = toOrderItemsArray(items)

  let total = 0

  for (const item of normalizedItems) {
    const parsedQuantity = Number(item.quantity ?? 0)
    total += Number.isFinite(parsedQuantity) ? parsedQuantity : 0
  }

  return total
}

const toOrderTotal = (total: WordPressMyOrderItem["total"]): number => {
  const parsed = Number(total ?? 0)
  return Number.isFinite(parsed) ? parsed : 0
}

export const GET = async (request: Request) => {
  const session = await getSession()
  const sessionToken = session?.wordpressSessionCookie?.trim()

  if (!sessionToken) {
    return toUnauthorizedResponse()
  }

  const url = new URL(request.url)
  const parsedLimit = limitSchema.safeParse(
    url.searchParams.get("limit") ?? "20"
  )

  if (!parsedLimit.success) {
    return NextResponse.json(
      {
        ok: false,
        code: "INVALID_PAYLOAD",
        message: "Parametro limit invalido para pedidos da conta.",
      },
      { status: 422, headers: NO_CACHE_HEADERS }
    )
  }

  try {
    const orders = await getWordPressAccountOrders(
      sessionToken,
      parsedLimit.data
    )

    return NextResponse.json(
      {
        ok: true,
        orders: orders.map((order) => {
          const id = Number(order.id ?? 0)

          return {
            id,
            number: `#${id}`,
            status: order.status,
            total: toOrderTotal(order.total),
            currency: order.currency ?? "BRL",
            dateCreated: order.date_created ?? "",
            itemCount: toItemCount(order.items),
          }
        }),
      },
      { headers: NO_CACHE_HEADERS }
    )
  } catch (error) {
    if (isUnauthorizedAxiosError(error)) {
      await clearSession()
      return toUnauthorizedResponse()
    }

    return NextResponse.json(
      {
        ok: false,
        code: "ACCOUNT_ORDERS_FETCH_FAILED",
        message: "Nao foi possivel carregar os pedidos da conta.",
      },
      { status: 502, headers: NO_CACHE_HEADERS }
    )
  }
}
